import type { MessageConfigInput } from "src/graphql";

/**
 * The `message_config` wire fields the journey save strips from clean
 * row-backed nodes (`transformMessageConfigToInput`) so a load-snapshot
 * buffer can't overwrite a fresher messages-area edit. A persist path that
 * edits one of these must mark the node content-dirty
 * (`persistMessageContentToNode`) or the save strips the edit.
 *
 * `push_config` counts as content even though it isn't user-typed: persists
 * re-derive it from the buffered push editor state, so on an unmarked node it
 * is load-snapshot content in disguise.
 *
 * `cep_whatsapp_template_id` travels with the `editor_state` it set.
 */
export const MESSAGE_CONTENT_FIELDS = [
  "subject",
  "preheader",
  "editor_state",
  "rendered_output",
  "profile_mappings",
  "push_config",
  "cep_whatsapp_template_id",
  "inapp_content",
] as const satisfies readonly (keyof MessageConfigInput)[];

export type MessageContentField = (typeof MESSAGE_CONTENT_FIELDS)[number];

/** Instructions to the journey save rather than tile state, so persists carry them forward until a save sends them. */
export const PENDING_SAVE_MESSAGE_CONFIG_FIELDS = [
  "cep_whatsapp_template_id",
] as const satisfies readonly (keyof MessageConfigInput)[];

export type PendingSaveMessageConfigField =
  (typeof PENDING_SAVE_MESSAGE_CONFIG_FIELDS)[number];

const CONTENT_FIELDS: ReadonlySet<string> = new Set(MESSAGE_CONTENT_FIELDS);

export const omitMessageContentFields = <T extends object>(
  config: T,
): Omit<T, MessageContentField> =>
  Object.fromEntries(
    Object.entries(config).filter(([key]) => !CONTENT_FIELDS.has(key)),
  ) as Omit<T, MessageContentField>;
